import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, CheckCircle, XCircle, AlertTriangle, Clock, Shield, Play, Ban, MessageSquare, CreditCard, RefreshCw, RotateCcw, FileText, Navigation, Send, ExternalLink, FileCheck } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetOrder, adminUpdateOrderStatus, adminValidateOrder, adminMockPayment, adminAddOrderNote, adminGetOrderPayments, adminRefundPayment } from '../../services/api';
import AdminDocumentPacket from './AdminDocumentPacket';

const BASE = process.env.REACT_APP_BACKEND_URL;

// Map order status → pipeline page with highlight param
function getPipelineRoute(order) {
  const { status, _id } = order;
  if (['ready_for_production', 'batched', 'in_production'].includes(status))
    return { path: `/admin/production?highlight=${_id}`, label: 'View in Production' };
  if (status === 'serialized')
    return { path: `/admin/serialization?highlight=${_id}`, label: 'View in Serialization' };
  if (['ready_to_ship', 'shipped'].includes(status))
    return { path: `/admin/shipping?highlight=${_id}`, label: 'View in Shipping' };
  return null;
}

const STATUS_CFG = {
  new:                  { label: 'New',                 color: 'text-zinc-400',  bg: 'bg-zinc-800 border-zinc-700' },
  payment_pending:      { label: 'Payment Pending',     color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
  payment_verified:     { label: 'Payment Verified',    color: 'text-blue-400',   bg: 'bg-blue-500/10 border-blue-500/20' },
  validation_pending:   { label: 'Needs Validation',    color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  validation_hold:      { label: 'Validation Hold',     color: 'text-red-400',    bg: 'bg-red-500/10 border-red-500/20' },
  ready_for_production: { label: 'Ready for Production', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
  batched:              { label: 'Cut Sheet Queue',         color: 'text-cyan-400',   bg: 'bg-cyan-500/10 border-cyan-500/20' },
  in_production:        { label: 'In Production',        color: 'text-blue-400',   bg: 'bg-blue-500/10 border-blue-500/20' },
  serialized:           { label: 'Serialized',           color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  packed:               { label: 'Packed',               color: 'text-indigo-400', bg: 'bg-indigo-500/10 border-indigo-500/20' },
  ready_to_ship:        { label: 'Ready to Ship',        color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
  on_hold:              { label: 'On Hold',              color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
  shipped:              { label: 'Shipped',              color: 'text-teal-400',   bg: 'bg-teal-500/10 border-teal-500/20' },
  cancelled:            { label: 'Cancelled',            color: 'text-zinc-500',   bg: 'bg-zinc-900 border-zinc-800' },
};

const VALID_TRANSITIONS_FE = {
  new:                  ['payment_pending', 'on_hold', 'cancelled'],
  payment_pending:      ['payment_verified', 'on_hold', 'cancelled'],
  payment_verified:     ['validation_pending', 'on_hold', 'cancelled'],
  validation_pending:   ['ready_for_production', 'validation_hold', 'on_hold', 'cancelled'],
  validation_hold:      ['ready_for_production', 'on_hold', 'cancelled'],
  ready_for_production: ['in_production', 'on_hold', 'cancelled'],  // skip batched — it's auto-set by cut sheet
  batched:              ['in_production', 'on_hold', 'cancelled'],   // manual override if needed
  in_production:        ['serialized', 'on_hold', 'cancelled'],
  serialized:           ['packed', 'ready_to_ship', 'on_hold', 'cancelled'],
  packed:               ['ready_to_ship', 'on_hold', 'cancelled'],
  ready_to_ship:        ['shipped', 'on_hold', 'cancelled'],
  on_hold:              ['payment_pending', 'payment_verified', 'validation_pending', 'validation_hold', 'ready_for_production', 'in_production', 'serialized', 'packed', 'ready_to_ship', 'cancelled'],
  shipped:              ['cancelled'],
  cancelled:            [],
};

function StatusBadge({ status, large }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.new;
  return (
    <span className={`font-inter font-bold px-2.5 py-1 border uppercase tracking-wider ${large ? 'text-xs' : 'text-[10px]'} ${cfg.color} ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
}

function IssueRow({ issue }) {
  const isError = issue.severity === 'error';
  return (
    <div className={`flex items-start gap-3 px-4 py-3 border-b border-white/5 last:border-0 ${isError ? 'bg-red-500/3' : 'bg-yellow-500/3'}`}>
      {isError ? <XCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" /> : <AlertTriangle size={14} className="text-yellow-400 flex-shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`font-inter text-[10px] font-bold uppercase tracking-wider ${isError ? 'text-red-400' : 'text-yellow-400'}`}>
            {issue.severity}
          </span>
          <span className="font-inter text-[10px] text-zinc-600 font-mono">{issue.code}</span>
        </div>
        <p className="font-inter text-sm text-white mt-0.5">{issue.message}</p>
        {issue.field && <p className="font-inter text-[11px] text-zinc-500 mt-0.5">Field: {issue.field}</p>}
      </div>
    </div>
  );
}

// ── Dealer FFL Status Card (for dealer orders — dealer IS the FFL) ─────────────
function DealerFflStatusCard({ order, token }) {
  const [dealer, setDealer] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!order.dealer_id || !token) return;
    fetch(`${BASE}/api/admin/dealers/${order.dealer_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDealer(d); })
      .catch(console.error);
  }, [order.dealer_id, token]);

  if (!dealer) return (
    <div className="bg-[#1a1a1a] border border-white/10 p-4 animate-pulse">
      <div className="h-4 bg-zinc-800 rounded w-48" />
    </div>
  );

  const fflPresent = !!dealer.ffl_number;
  const fflExpiry = dealer.ffl_expiry;
  const fflExpired = fflExpiry ? new Date(fflExpiry) < new Date() : false;
  const fflValid = fflPresent && !fflExpired;
  const sotValid = dealer.sot_status === 'yes';
  const bothValid = fflValid && sotValid;

  // FFL card documents uploaded by the dealer
  const fflDocs = (dealer.documents || []).filter(d =>
    d.document_type === 'ffl' || d.original_name?.toLowerCase().includes('ffl')
  );
  const sotDocs = (dealer.documents || []).filter(d =>
    d.document_type === 'sot' || d.original_name?.toLowerCase().includes('sot')
  );

  return (
    <div className={`bg-[#1a1a1a] border overflow-hidden ${bothValid ? 'border-green-500/30' : 'border-orange-500/30'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Shield size={15} className="text-orange-500" />
          <h3 className="font-bebas text-base text-white tracking-wider">DEALER FFL STATUS</h3>
          <span className="font-inter text-[9px] text-teal-400 border border-teal-500/30 bg-teal-500/10 px-2 py-0.5 uppercase tracking-wider">
            Direct Shipment — Dealer is Licensed FFL
          </span>
          {bothValid && <span className="font-inter text-[9px] text-green-400 border border-green-500/30 bg-green-500/10 px-2 py-0.5 uppercase tracking-wider">Verified</span>}
        </div>
        <button onClick={() => setExpanded(e => !e)}
          className="font-inter text-[10px] text-zinc-500 hover:text-white border border-white/10 hover:border-white/30 px-2 py-1 transition-colors uppercase tracking-wider">
          {expanded ? 'Hide Details ▲' : 'View FFL / SOT ▼'}
        </button>
      </div>

      {/* Summary row: dealer info + two status badges */}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Dealer identity */}
          <div className="flex-1 min-w-0">
            <p className="font-inter text-sm text-white font-semibold">{dealer.business_name}</p>
            <p className="font-inter text-xs text-zinc-400 mt-0.5">
              {[dealer.business_address, dealer.city, dealer.state, dealer.zip_code].filter(Boolean).join(', ')}
            </p>
            <div className="flex gap-4 mt-1.5 flex-wrap">
              {dealer.email && <p className="font-inter text-[10px] text-zinc-500">{dealer.email}</p>}
              {dealer.phone && <p className="font-inter text-[10px] text-zinc-500">{dealer.phone}</p>}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Link
                to={`/admin/dealers/${dealer._id}`}
                className="flex items-center gap-1 font-inter text-[10px] text-orange-400 hover:text-orange-300 border border-orange-500/20 px-2 py-0.5 transition-colors"
              >
                <ExternalLink size={9} />View Dealer Profile
              </Link>
            </div>
          </div>

          {/* Two status indicators */}
          <div className="flex gap-3 flex-shrink-0">
            {/* FFL License */}
            <div className={`flex flex-col items-center gap-1 px-3 py-2.5 border ${
              fflValid ? 'border-green-500/40 bg-green-500/10 text-green-400' : 'border-red-500/40 bg-red-500/10 text-red-400'
            }`}>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${fflValid ? 'border-green-500 bg-green-500' : 'border-red-500'}`}>
                {fflValid ? <CheckCircle size={14} className="text-white" /> : <XCircle size={14} className="text-red-400" />}
              </div>
              <span className="font-inter text-[9px] uppercase tracking-wider whitespace-nowrap text-center">
                FFL<br/>License
              </span>
            </div>

            {/* SOT Status */}
            <div className={`flex flex-col items-center gap-1 px-3 py-2.5 border ${
              sotValid ? 'border-green-500/40 bg-green-500/10 text-green-400' : 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400'
            }`}>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${sotValid ? 'border-green-500 bg-green-500' : 'border-yellow-500'}`}>
                {sotValid ? <CheckCircle size={14} className="text-white" /> : <AlertTriangle size={14} className="text-yellow-400" />}
              </div>
              <span className="font-inter text-[9px] uppercase tracking-wider whitespace-nowrap text-center">
                SOT<br/>Form
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable: FFL + SOT details + document links */}
      {expanded && (
        <div className="border-t border-white/10 bg-[#141414]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {/* FFL Details */}
            <div className="px-5 py-4">
              <p className="font-bebas text-sm text-orange-400 tracking-wider mb-3 flex items-center gap-2">
                <FileCheck size={13} />FFL LICENSE DETAILS
              </p>
              <div className="space-y-2 mb-4">
                {[
                  { label: 'Business Name', value: dealer.business_name },
                  { label: 'FFL Number', value: dealer.ffl_number || '— Not on file' },
                  { label: 'License Type', value: 'Type 01 — Dealer in Firearms' },
                  { label: 'Expiry Date', value: dealer.ffl_expiry ? `${dealer.ffl_expiry}${fflExpired ? ' ⚠ EXPIRED' : ' ✓'}` : '— Not on file' },
                  { label: 'Address', value: [dealer.business_address, dealer.city, dealer.state, dealer.zip_code].filter(Boolean).join(', ') },
                ].map(r => (
                  <div key={r.label} className="flex items-start gap-2">
                    <span className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider w-24 flex-shrink-0 mt-0.5">{r.label}</span>
                    <span className={`font-inter text-xs break-all ${r.value?.includes('EXPIRED') ? 'text-red-400' : r.value?.includes('✓') ? 'text-green-400' : 'text-zinc-300'}`}>{r.value}</span>
                  </div>
                ))}
              </div>
              {/* FFL document links */}
              {fflDocs.length > 0 ? (
                <div>
                  <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider mb-1.5">FFL License Documents</p>
                  <div className="space-y-1">
                    {fflDocs.map((doc, i) => (
                      <a key={i} href={`${BASE}${doc.url}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 font-inter text-xs text-orange-400 hover:text-orange-300 transition-colors">
                        <ExternalLink size={10} />
                        {doc.original_name || 'FFL License Card'}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="font-inter text-[10px] text-zinc-600">No FFL document uploaded yet</p>
              )}
            </div>

            {/* SOT Details */}
            <div className="px-5 py-4">
              <p className="font-bebas text-sm text-orange-400 tracking-wider mb-3 flex items-center gap-2">
                <FileCheck size={13} />SOT / CLASS III STATUS
              </p>
              <div className="space-y-2 mb-4">
                {[
                  { label: 'SOT Status', value: dealer.sot_status === 'yes' ? 'Active SOT ✓' : dealer.sot_status === 'no' ? 'No SOT on file' : dealer.sot_status || '— Not specified' },
                  { label: 'SOT Class', value: 'Class 3 — Dealer (NFA Items)' },
                  { label: 'Annual Fee', value: '$500 (Class 3 Dealer)' },
                  { label: 'Dealer Status', value: dealer.status?.toUpperCase() || '—' },
                  { label: 'Tax ID', value: dealer.tax_id || '— Not on file' },
                ].map(r => (
                  <div key={r.label} className="flex items-start gap-2">
                    <span className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider w-24 flex-shrink-0 mt-0.5">{r.label}</span>
                    <span className={`font-inter text-xs ${r.value?.includes('✓') ? 'text-green-400' : 'text-zinc-300'}`}>{r.value}</span>
                  </div>
                ))}
              </div>
              {/* SOT document links */}
              {sotDocs.length > 0 ? (
                <div>
                  <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider mb-1.5">SOT Documents</p>
                  <div className="space-y-1">
                    {sotDocs.map((doc, i) => (
                      <a key={i} href={`${BASE}${doc.url}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 font-inter text-xs text-orange-400 hover:text-orange-300 transition-colors">
                        <ExternalLink size={10} />
                        {doc.original_name || 'SOT Registration'}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="font-inter text-[10px] text-zinc-600">No SOT document uploaded yet</p>
              )}
            </div>
          </div>

          {/* Direct shipment note */}
          <div className="px-5 py-3 border-t border-white/5 bg-teal-500/5">
            <p className="font-inter text-[10px] text-teal-300 leading-relaxed">
              <strong>Direct Dealer Shipment:</strong> This is a dealer order — the suppressor ships
              directly to the dealer's licensed FFL address. No separate Form 4 transfer is required
              since the dealer holds their own FFL/SOT. Dealer takes possession and handles their own
              A&D disposition records.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── FFL Transfer Dealer Card (for public/customer orders) ─────────────────────
function FflDealerCard({ order, token, BASE, onUpdated }) {
  const ffl = order.ffl_dealer || {};
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState('');

  const toggle = async (field, currentVal) => {
    setSaving(field);
    try {
      await fetch(`${BASE}/api/admin/orders/${order._id}/ffl-verify`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: !currentVal }),
      });
      onUpdated();
    } catch (e) { alert(e.message); }
    setSaving('');
  };

  const fflVerified = !!ffl.ffl_verified;
  const sotConfirmed = !!ffl.sot_confirmed;
  const bothDone = fflVerified && sotConfirmed;

  return (
    <div className={`bg-[#1a1a1a] border overflow-hidden ${bothDone ? 'border-green-500/30' : 'border-orange-500/30'}`}>
      {/* Header row */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Shield size={15} className="text-orange-500" />
          <h3 className="font-bebas text-base text-white tracking-wider">FFL TRANSFER DEALER</h3>
          {bothDone && <span className="font-inter text-[10px] text-green-400 border border-green-500/30 bg-green-500/10 px-2 py-0.5 uppercase tracking-wider">Verified</span>}
        </div>
        <button onClick={() => setExpanded(e => !e)}
          className="font-inter text-[10px] text-zinc-500 hover:text-white border border-white/10 hover:border-white/30 px-2 py-1 transition-colors uppercase tracking-wider">
          {expanded ? 'Hide Details ▲' : 'View Details ▼'}
        </button>
      </div>

      {/* Main dealer info + two checkmarks */}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Dealer identity */}
          <div className="flex-1 min-w-0">
            <p className="font-inter text-sm text-white font-semibold">{ffl.business_name}</p>
            <p className="font-inter text-xs text-zinc-400 mt-0.5">
              {[ffl.address, ffl.city, ffl.state, ffl.zip].filter(Boolean).join(', ')}
            </p>
            {ffl.phone && <p className="font-inter text-xs text-zinc-500 mt-0.5">{ffl.phone}</p>}
            {ffl.license_number && (
              <p className="font-inter text-[10px] text-zinc-600 mt-1 font-mono">FFL: {ffl.license_number}</p>
            )}
          </div>

          {/* Two verification checkmarks — click to toggle */}
          <div className="flex gap-3 flex-shrink-0">
            {/* FFL Registration */}
            <button
              data-testid="ffl-verified-toggle"
              onClick={() => toggle('ffl_verified', fflVerified)}
              disabled={saving === 'ffl_verified'}
              title={fflVerified ? `Verified by ${ffl.ffl_verified_by || 'admin'} — click to unverify` : 'Click to mark FFL registration verified'}
              className={`flex flex-col items-center gap-1 px-3 py-2.5 border transition-all ${
                fflVerified
                  ? 'border-green-500/40 bg-green-500/10 text-green-400'
                  : 'border-zinc-700 bg-[#111] text-zinc-600 hover:border-orange-500/40 hover:text-orange-400'
              } ${saving === 'ffl_verified' ? 'opacity-60' : 'cursor-pointer'}`}
            >
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${fflVerified ? 'border-green-500 bg-green-500' : 'border-zinc-600'}`}>
                {fflVerified && <CheckCircle size={14} className="text-white" />}
              </div>
              <span className="font-inter text-[9px] uppercase tracking-wider whitespace-nowrap">
                FFL<br/>Registration
              </span>
            </button>

            {/* SOT Confirmed */}
            <button
              data-testid="sot-confirmed-toggle"
              onClick={() => toggle('sot_confirmed', sotConfirmed)}
              disabled={saving === 'sot_confirmed'}
              title={sotConfirmed ? `SOT confirmed by ${ffl.sot_confirmed_by || 'admin'} — click to unconfirm` : 'Click to mark SOT (Class III) confirmed'}
              className={`flex flex-col items-center gap-1 px-3 py-2.5 border transition-all ${
                sotConfirmed
                  ? 'border-green-500/40 bg-green-500/10 text-green-400'
                  : 'border-zinc-700 bg-[#111] text-zinc-600 hover:border-orange-500/40 hover:text-orange-400'
              } ${saving === 'sot_confirmed' ? 'opacity-60' : 'cursor-pointer'}`}
            >
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${sotConfirmed ? 'border-green-500 bg-green-500' : 'border-zinc-600'}`}>
                {sotConfirmed && <CheckCircle size={14} className="text-white" />}
              </div>
              <span className="font-inter text-[9px] uppercase tracking-wider whitespace-nowrap">
                SOT<br/>Form
              </span>
            </button>
          </div>
        </div>

        {/* Verification timestamps */}
        {(ffl.ffl_verified_by || ffl.sot_confirmed_by) && (
          <div className="mt-3 flex gap-4 flex-wrap">
            {ffl.ffl_verified_by && (
              <p className="font-inter text-[10px] text-zinc-600">
                FFL verified by <span className="text-zinc-500">{ffl.ffl_verified_by}</span>
                {ffl.ffl_verified_at ? ` · ${new Date(ffl.ffl_verified_at).toLocaleDateString()}` : ''}
              </p>
            )}
            {ffl.sot_confirmed_by && (
              <p className="font-inter text-[10px] text-zinc-600">
                SOT confirmed by <span className="text-zinc-500">{ffl.sot_confirmed_by}</span>
                {ffl.sot_confirmed_at ? ` · ${new Date(ffl.sot_confirmed_at).toLocaleDateString()}` : ''}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Expandable FFL + SOT detail panel */}
      {expanded && (
        <div className="border-t border-white/10 bg-[#141414]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {/* FFL License Details */}
            <div className="px-5 py-4">
              <p className="font-bebas text-sm text-orange-400 tracking-wider mb-3">FFL LICENSE DETAILS</p>
              <div className="space-y-2">
                {[
                  { label: 'Business Name', value: ffl.business_name },
                  { label: 'FFL Number', value: ffl.license_number || '—' },
                  { label: 'License Type', value: 'Type 01 — Dealer in Firearms' },
                  { label: 'License Expiry', value: ffl.license_expiry || 'On file at dealer' },
                  { label: 'Address', value: [ffl.address, ffl.city, ffl.state, ffl.zip].filter(Boolean).join(', ') },
                  { label: 'Phone', value: ffl.phone || '—' },
                ].map(r => (
                  <div key={r.label} className="flex items-start gap-2">
                    <span className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider w-24 flex-shrink-0 mt-0.5">{r.label}</span>
                    <span className="font-inter text-xs text-zinc-300 break-all">{r.value}</span>
                  </div>
                ))}
              </div>
              <div className={`mt-3 flex items-center gap-2 px-2 py-1.5 border ${fflVerified ? 'border-green-500/30 bg-green-500/5' : 'border-zinc-700 bg-[#111]'}`}>
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${fflVerified ? 'bg-green-500' : 'bg-zinc-700'}`} />
                <span className={`font-inter text-[10px] ${fflVerified ? 'text-green-400' : 'text-zinc-600'}`}>
                  {fflVerified ? `FFL Registration Verified — ${ffl.ffl_verified_by || 'Admin'}` : 'FFL Registration Not Yet Verified'}
                </span>
              </div>
            </div>

            {/* SOT / Class III Details */}
            <div className="px-5 py-4">
              <p className="font-bebas text-sm text-orange-400 tracking-wider mb-3">SOT / CLASS III STATUS</p>
              <div className="space-y-2">
                {[
                  { label: 'SOT Class', value: 'Class 3 — Dealer (NFA items)' },
                  { label: 'Tax Year', value: ffl.sot_tax_year || '2026 (July 1, 2025 – June 30, 2026)' },
                  { label: 'SOT Reg. Date', value: ffl.sot_reg_date || 'July 1, 2025 (Est.)' },
                  { label: 'Annual SOT Fee', value: '$500 (Class 3 Dealer)' },
                  { label: 'NFA Transfers', value: 'Suppressors, SBR, SBS, MG' },
                  { label: 'Form Required', value: 'ATF Form 4 (Civilian Transfer)' },
                ].map(r => (
                  <div key={r.label} className="flex items-start gap-2">
                    <span className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider w-24 flex-shrink-0 mt-0.5">{r.label}</span>
                    <span className="font-inter text-xs text-zinc-300">{r.value}</span>
                  </div>
                ))}
              </div>
              <div className={`mt-3 flex items-center gap-2 px-2 py-1.5 border ${sotConfirmed ? 'border-green-500/30 bg-green-500/5' : 'border-zinc-700 bg-[#111]'}`}>
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${sotConfirmed ? 'bg-green-500' : 'bg-zinc-700'}`} />
                <span className={`font-inter text-[10px] ${sotConfirmed ? 'text-green-400' : 'text-zinc-600'}`}>
                  {sotConfirmed ? `SOT Class 3 Confirmed — ${ffl.sot_confirmed_by || 'Admin'}` : 'SOT Status Not Yet Confirmed'}
                </span>
              </div>
            </div>
          </div>

          {/* NFA Transfer note */}
          <div className="px-5 py-3 border-t border-white/5 bg-orange-500/5">
            <p className="font-inter text-[10px] text-orange-300 leading-relaxed">
              <strong>NFA Transfer Note:</strong> The suppressor ships to this dealer. The customer will complete
              ATF Form 4 at this location. ATF approval (days–months) required before the customer can take possession.
              Verify FFL + SOT status before releasing the shipment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const { token, user } = useAdminAuth();
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionNote, setActionNote] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [replyMsg, setReplyMsg] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const BASE = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    Promise.all([adminGetOrder(token, id), adminGetOrderPayments(token, id)])
      .then(([o, p]) => { setOrder(o); setNote(o.admin_note || ''); setPayments(p); setMessages(o.messages || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id, token]);

  const handleAdminReply = async () => {
    if (!replyMsg.trim()) return;
    setSendingReply(true);
    try {
      const res = await fetch(`${BASE}/api/admin/orders/${id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: replyMsg.trim() }),
      });
      if (res.ok) {
        setReplyMsg('');
        load();
      }
    } catch (e) { console.error(e); }
    setSendingReply(false);
  };

  const doAction = async (fn, label) => {
    setSaving(label);
    setError('');
    try {
      const updated = await fn();
      setOrder(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  };

  const handleStatusChange = (newStatus) =>
    doAction(() => adminUpdateOrderStatus(token, id, newStatus, actionNote), newStatus);

  const handleValidate = () =>
    doAction(() => adminValidateOrder(token, id), 'validate');

  const handlePayment = (success) =>
    doAction(async () => {
      const res = await adminMockPayment(token, id, success);
      // New response includes both order + payment
      if (res.order) {
        setPayments(prev => {
          const existing = prev.find(p => p.payment_id === res.payment?.payment_id);
          if (existing) return prev.map(p => p.payment_id === res.payment.payment_id ? res.payment : p);
          return res.payment ? [res.payment, ...prev] : prev;
        });
        return res.order;
      }
      return res;
    }, `payment_${success ? 'ok' : 'fail'}`);

  const handleNote = async () => {
    if (!note.trim()) return;
    setSaving('note');
    try { const u = await adminAddOrderNote(token, id, note); setOrder(u); } catch (e) { setError(e.message); }
    setSaving('');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!order) {
    return <div className="text-center py-12"><p className="font-inter text-sm text-zinc-600">{error || 'Order not found'}</p><Link to="/admin/orders" className="font-inter text-xs text-orange-500 mt-2 block">← Back</Link></div>;
  }

  const transitions = VALID_TRANSITIONS_FE[order.status] || [];
  const val = order.validation || {};
  const timeline = [...(order.timeline || [])].reverse();

  return (
    <div data-testid="admin-order-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/orders" className="text-zinc-500 hover:text-white"><ChevronLeft size={20} /></Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-bebas text-3xl text-white tracking-wider">{order.order_number}</h1>
              <StatusBadge status={order.status} large />
              <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${
                order.type === 'dealer' ? 'text-orange-400 bg-orange-500/10 border-orange-500/20' : 'text-blue-400 bg-blue-500/10 border-blue-500/20'
              }`}>{order.type || 'public'}</span>
              {/* Pipeline deep-link */}
              {(() => {
                const route = getPipelineRoute(order);
                return route ? (
                  <Link
                    to={route.path}
                    data-testid="pipeline-track-link"
                    className="flex items-center gap-1.5 font-inter text-xs text-orange-400 hover:text-orange-300 border border-orange-500/30 hover:border-orange-500 px-2.5 py-1 transition-colors"
                    title={route.label}
                  >
                    <Navigation size={11} />{route.label} →
                  </Link>
                ) : null;
              })()}
            </div>
            <p className="font-inter text-xs text-zinc-500 mt-1">
              {order.business_name || order.customer_info?.name} · Created {order.created_at ? new Date(order.created_at).toLocaleString() : '—'}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">
          <XCircle size={14} />{error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* ===== LEFT: Order Info ===== */}
        <div className="xl:col-span-2 space-y-5">
          {/* Items */}
          <div className="bg-[#1a1a1a] border border-white/10">
            <div className="px-5 py-4 border-b border-white/10">
              <h2 className="font-bebas text-lg text-white tracking-wider">ORDER ITEMS ({order.items?.length || 0})</h2>
            </div>
            <div className="divide-y divide-white/5">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3">
                  <div className="w-12 h-12 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                    <img src={item.product_image} alt="" className="w-full h-full object-contain p-0.5" onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-inter text-sm text-white">{item.product_name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="font-inter text-xs text-zinc-500">Qty: {item.quantity}</span>
                      <span className="font-inter text-xs text-zinc-500">× ${item.unit_price?.toFixed(2)}</span>
                      {item.nfa_item && <span className="flex items-center gap-1 font-inter text-[10px] text-orange-400"><Shield size={9} />NFA</span>}
                    </div>
                  </div>
                  <span className="font-bebas text-base text-white">${item.total?.toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between px-5 py-3 bg-[#141414] border-t border-white/10">
              <span className="font-inter text-sm text-zinc-400">Subtotal</span>
              <span className="font-bebas text-xl text-orange-500 tracking-wide">${order.subtotal?.toFixed(2)}</span>
            </div>
          </div>

          {/* Customer / Dealer + Physical Address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-[#1a1a1a] border border-white/10 p-5">
              <h3 className="font-bebas text-base text-white tracking-wider mb-3">
                {order.type === 'dealer' ? 'DEALER INFO' : 'CUSTOMER INFO'}
              </h3>
              <div className="space-y-2">
                {[
                  { label: 'Business/Name', value: order.business_name || order.customer_info?.name },
                  { label: 'Email', value: order.customer_info?.email },
                  { label: 'Phone', value: order.customer_info?.phone },
                  ...(order.type === 'dealer' ? [
                    { label: 'Dealer ID', value: order.dealer_id },
                  ] : []),
                ].filter(f => f.value).map(f => (
                  <div key={f.label} className="flex items-start gap-2">
                    <span className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider w-20 flex-shrink-0 mt-0.5">{f.label}</span>
                    <span className="font-inter text-xs text-zinc-300 break-all">{f.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#1a1a1a] border border-white/10 p-5">
              <h3 className="font-bebas text-base text-white tracking-wider mb-3">
                {order.type === 'dealer' ? 'DEALER SHIPPING ADDRESS' : 'CUSTOMER PHYSICAL ADDRESS'}
              </h3>
              {order.shipping_address ? (
                <div className="space-y-1">
                  {['address', 'city', 'state', 'zip'].map(f => (
                    order.shipping_address[f] && (
                      <div key={f} className="flex items-start gap-2">
                        <span className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider w-12 flex-shrink-0">{f}</span>
                        <span className="font-inter text-xs text-zinc-300">{order.shipping_address[f]}</span>
                      </div>
                    )
                  ))}
                  {order.has_nfa_items && (
                    <div className="mt-3 flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-3 py-2">
                      <Shield size={12} className="text-orange-500" />
                      <span className="font-inter text-xs text-orange-400">UPS shipping required (NFA items)</span>
                    </div>
                  )}
                </div>
              ) : <p className="font-inter text-sm text-zinc-600">No address provided</p>}
            </div>
          </div>

          {/* FFL section — dealer orders show dealer's own FFL, public orders show transfer dealer */}
          {order.type === 'dealer' ? (
            <DealerFflStatusCard order={order} token={token} />
          ) : order.ffl_dealer?.business_name ? (
            <FflDealerCard order={order} token={token} BASE={BASE} onUpdated={load} />
          ) : order.has_nfa_items ? (
            <div className="bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3">
              <Shield size={16} className="text-red-400 flex-shrink-0" />
              <div>
                <p className="font-bebas text-base text-red-400 tracking-wider">NO FFL DEALER ON FILE</p>
                <p className="font-inter text-xs text-zinc-500">This NFA order has no FFL transfer dealer assigned. This must be resolved before shipping.</p>
              </div>
            </div>
          ) : null}

          {/* Payment Info */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bebas text-base text-white tracking-wider">PAYMENT STATUS</h3>
              <span className={`font-inter text-xs font-bold px-2 py-0.5 border uppercase tracking-wider ${
                order.payment_status === 'paid' ? 'text-green-400 bg-green-500/10 border-green-500/20' :
                order.payment_status === 'failed' ? 'text-red-400 bg-red-500/10 border-red-500/20' :
                'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'
              }`}>{order.payment_status || 'pending'}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Method', value: order.payment_method || 'invoice' },
                { label: 'Amount', value: `$${order.subtotal?.toFixed(2)}` },
              ].map(f => (
                <div key={f.label}>
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">{f.label}</p>
                  <p className="font-inter text-sm text-white mt-0.5">{f.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-[#1a1a1a] border border-white/10">
            <div className="px-5 py-4 border-b border-white/10">
              <h2 className="font-bebas text-lg text-white tracking-wider">TIMELINE</h2>
            </div>
            <div data-testid="order-timeline" className="px-5 py-4 space-y-3 max-h-64 overflow-y-auto">
              {timeline.map((ev, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                    ev.actor === 'system' ? 'bg-zinc-600' :
                    ev.event?.includes('status') ? 'bg-orange-500' :
                    ev.event?.includes('payment') ? 'bg-green-500' :
                    ev.event?.includes('validation') ? 'bg-purple-500' : 'bg-zinc-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-inter text-sm text-zinc-300">{ev.description}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="font-inter text-[10px] text-zinc-600">
                        {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}
                      </span>
                      <span className="font-inter text-[10px] text-zinc-700">by {ev.actor}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ===== RIGHT: Validation + Actions ===== */}
        <div className="space-y-4">
          {/* Validation Results */}
          <div className="bg-[#1a1a1a] border border-white/10">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h2 className="font-bebas text-lg text-white tracking-wider">VALIDATION</h2>
              <button
                data-testid="run-validation-button"
                onClick={handleValidate}
                disabled={saving === 'validate'}
                className="flex items-center gap-1.5 font-inter text-xs text-orange-500 hover:text-white border border-orange-500/40 hover:border-orange-500 px-3 py-1.5 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} className={saving === 'validate' ? 'animate-spin' : ''} />
                {saving === 'validate' ? 'Running...' : 'Run Check'}
              </button>
            </div>
            <div className="p-5">
              {!val.checked_at ? (
                <div className="text-center py-6">
                  <Clock size={28} className="text-zinc-700 mx-auto mb-2" />
                  <p className="font-inter text-sm text-zinc-600">Validation not yet run</p>
                  <p className="font-inter text-xs text-zinc-700 mt-1">Click 'Run Check' to validate this order</p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className={`flex items-center gap-3 p-3 border mb-4 ${
                    val.is_valid ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    {val.is_valid
                      ? <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                      : <XCircle size={18} className="text-red-500 flex-shrink-0" />
                    }
                    <div>
                      <p className={`font-bebas text-base tracking-wider ${val.is_valid ? 'text-green-400' : 'text-red-400'}`}>
                        {val.is_valid ? 'VALIDATION PASSED' : 'VALIDATION FAILED'}
                      </p>
                      <p className="font-inter text-[11px] text-zinc-500">
                        {val.error_count || 0} errors · {val.warning_count || 0} warnings · checked {val.checked_at ? new Date(val.checked_at).toLocaleTimeString() : '—'}
                      </p>
                    </div>
                  </div>
                  {/* Issue list */}
                  {val.issues?.length > 0 ? (
                    <div className="bg-[#141414] border border-white/5">
                      {val.issues.map((issue, i) => <IssueRow key={i} issue={issue} />)}
                    </div>
                  ) : (
                    <p className="font-inter text-sm text-green-400 text-center py-3">All checks passed — no issues found</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Mock Payment */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-3 flex items-center gap-2">
              <CreditCard size={16} className="text-orange-500" />PAYMENT PROCESSING
            </h2>
            <p className="font-inter text-xs text-zinc-500 mb-4">Simulate payment success or failure for testing the order pipeline.</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="mock-payment-success"
                onClick={() => handlePayment(true)}
                disabled={!!saving || order.payment_status === 'paid'}
                className="flex items-center justify-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 font-inter text-sm font-medium py-2.5 transition-colors disabled:opacity-40"
              >
                <Play size={12} />
                {saving === 'payment_ok' ? 'Processing...' : 'Payment OK'}
              </button>
              <button
                data-testid="mock-payment-fail"
                onClick={() => handlePayment(false)}
                disabled={!!saving || order.payment_status === 'paid'}
                className="flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-inter text-sm font-medium py-2.5 transition-colors disabled:opacity-40"
              >
                <Ban size={12} />
                {saving === 'payment_fail' ? 'Processing...' : 'Payment Fail'}
              </button>
            </div>
            {order.payment_status === 'paid' && (
              <p className="font-inter text-xs text-green-400 text-center mt-2">Payment already verified</p>
            )}
          </div>

          {/* State Machine — redesigned for clarity */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-1">ORDER STATUS</h2>
            <p className="font-inter text-[11px] text-zinc-600 mb-4">
              Pre-production states are manual. Production stages are driven by the workflow modules.
            </p>

            {/* CASE A: Post-production state — workflow modules own this */}
            {['batched','in_production','serialized','packed','ready_to_ship','shipped'].includes(order.status) && (
              <div className="space-y-3">
                {/* Context: which module owns this state */}
                {({
                  batched:         { label: 'Cut Sheet Queue — managed by Cut Sheet Engine', href: '/admin/production', btnLabel: 'Go to Cut Sheet Engine' },
                  in_production:   { label: 'On the Production Floor — managed by Shop Floor', href: '/admin/shop-floor', btnLabel: 'Go to Shop Floor' },
                  serialized:      { label: 'Awaiting Serial Sign-Off — managed by Serialization', href: '/admin/serialization', btnLabel: 'Go to Serialization' },
                  packed:          { label: 'Packed — managed by Shipping', href: '/admin/shipping', btnLabel: 'Go to Shipping' },
                  ready_to_ship:   { label: 'Ready to Ship — managed by Shipping', href: '/admin/shipping', btnLabel: 'Go to Shipping' },
                  shipped:         { label: 'Shipped — order complete', href: '/admin/shipping', btnLabel: 'View in Shipping' },
                }[order.status]) && (() => {
                  const ctx = {
                    batched:         { label: 'Cut Sheet Queue — managed by Cut Sheet Engine', href: '/admin/production', btnLabel: 'Go to Cut Sheet Engine' },
                    in_production:   { label: 'On the Production Floor — managed by Shop Floor', href: '/admin/shop-floor', btnLabel: 'Go to Shop Floor' },
                    serialized:      { label: 'Awaiting Serial Sign-Off — managed by Serialization', href: '/admin/serialization', btnLabel: 'Go to Serialization' },
                    packed:          { label: 'Packed — managed by Shipping', href: '/admin/shipping', btnLabel: 'Go to Shipping' },
                    ready_to_ship:   { label: 'Ready to Ship — managed by Shipping', href: '/admin/shipping', btnLabel: 'Go to Shipping' },
                    shipped:         { label: 'Shipped — order complete', href: '/admin/shipping', btnLabel: 'View in Shipping' },
                  }[order.status];
                  return (
                    <div className="bg-[#111111] border border-white/10 px-4 py-3 flex items-center justify-between gap-3">
                      <p className="font-inter text-xs text-zinc-400">{ctx.label}</p>
                      <a href={ctx.href} className="font-inter text-xs text-orange-500 hover:text-white border border-orange-500/30 px-3 py-1.5 transition-colors whitespace-nowrap">
                        {ctx.btnLabel} →
                      </a>
                    </div>
                  );
                })()}

                {/* Emergency controls — always available */}
                <div className="border-t border-white/10 pt-3">
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Emergency Controls</p>
                  <div>
                    <input type="text" value={actionNote} onChange={e => setActionNote(e.target.value)}
                      className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-xs px-3 py-2 outline-none mb-2 placeholder-zinc-600"
                      placeholder="Reason for status change (required for reversions)..." />
                    <div className="grid grid-cols-2 gap-2">
                      {order.status !== 'on_hold' && (
                        <button data-testid="transition-to-on_hold" onClick={() => handleStatusChange('on_hold')} disabled={!!saving}
                          className="flex items-center justify-center gap-2 px-3 py-2.5 border font-inter text-sm font-medium transition-colors disabled:opacity-40 text-orange-400 bg-orange-500/10 border-orange-500/20 hover:bg-orange-500/20">
                          ⏸ Place On Hold
                        </button>
                      )}
                      {order.status !== 'cancelled' && order.status !== 'shipped' && (
                        <button data-testid="transition-to-cancelled" onClick={() => { if (window.confirm('Cancel this order? This cannot be easily undone.')) handleStatusChange('cancelled'); }} disabled={!!saving}
                          className="flex items-center justify-center gap-2 px-3 py-2.5 border font-inter text-sm font-medium transition-colors disabled:opacity-40 text-red-400 bg-red-500/10 border-red-500/20 hover:bg-red-500/20">
                          ✕ Cancel Order
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Revert — move back to a previous state */}
                {order.status !== 'shipped' && order.status !== 'cancelled' && (
                  <div className="border-t border-white/10 pt-3">
                    <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">↩ Revert to Earlier Stage</p>
                    <p className="font-inter text-[11px] text-zinc-600 mb-2">Use if something was set in error. The order returns to "Waiting for Cut Sheet."</p>
                    <button
                      data-testid="revert-to-waiting"
                      onClick={() => {
                        if (!actionNote.trim()) { alert('Please enter a reason in the note field before reverting.'); return; }
                        if (window.confirm(`Revert this order to "Waiting for Cut Sheet"? Reason: "${actionNote}"`)) {
                          handleStatusChange('ready_for_production');
                        }
                      }}
                      disabled={!!saving}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border font-inter text-sm font-medium transition-colors disabled:opacity-40 text-zinc-400 border-zinc-700 hover:border-white/30 hover:text-white hover:bg-white/5"
                    >
                      {saving === 'ready_for_production' ? 'Reverting...' : '↩ Revert to Waiting for Cut Sheet'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* CASE B: Pre-production state — show all manual transitions */}
            {!['batched','in_production','serialized','packed','ready_to_ship','shipped'].includes(order.status) && (
              <div className="space-y-2">
                <div>
                  <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Note (optional)</label>
                  <input data-testid="transition-note-input" type="text" value={actionNote} onChange={e => setActionNote(e.target.value)}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-xs px-3 py-2 outline-none transition-colors placeholder-zinc-600 mb-2"
                    placeholder="Optional note for this transition..." />
                </div>
                {(VALID_TRANSITIONS_FE[order.status] || []).map(t => {
                  const cfg = STATUS_CFG[t];
                  return (
                    <button key={t} data-testid={`transition-to-${t}`}
                      onClick={() => handleStatusChange(t)} disabled={!!saving}
                      className={`w-full flex items-center justify-between px-4 py-2.5 border font-inter text-sm font-medium transition-colors disabled:opacity-40 hover:border-white/30 hover:bg-white/5 ${cfg?.bg || 'bg-zinc-800 border-zinc-700'} ${cfg?.color || 'text-zinc-400'}`}>
                      <span>{saving === t ? 'Updating...' : `→ ${cfg?.label || t}`}</span>
                    </button>
                  );
                })}

                {/* Revert back one step */}
                {order.status !== 'new' && order.status !== 'on_hold' && order.status !== 'cancelled' && (() => {
                  const prevMap = { payment_pending: 'new', payment_verified: 'payment_pending', validation_pending: 'payment_verified', validation_hold: 'payment_verified', ready_for_production: 'payment_verified' };
                  const prevStatus = prevMap[order.status];
                  if (!prevStatus) return null;
                  return (
                    <div className="border-t border-white/5 pt-2 mt-1">
                      <button data-testid={`revert-to-${prevStatus}`}
                        onClick={() => { if (window.confirm(`Undo — revert to "${STATUS_CFG[prevStatus]?.label || prevStatus}"?`)) handleStatusChange(prevStatus); }}
                        disabled={!!saving}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-zinc-800 font-inter text-xs text-zinc-600 hover:text-zinc-400 hover:border-zinc-600 transition-colors disabled:opacity-40">
                        ↩ Undo — revert to {STATUS_CFG[prevStatus]?.label || prevStatus}
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Admin Note */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-3 flex items-center gap-2">
              <MessageSquare size={16} className="text-orange-500" />ADMIN NOTE
            </h2>
            <textarea
              data-testid="admin-note-textarea"
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors resize-none placeholder-zinc-600 mb-3"
              placeholder="Internal notes (not visible to dealer)..."
            />
            <button
              data-testid="save-note-button"
              onClick={handleNote}
              disabled={saving === 'note'}
              className="w-full bg-zinc-700 hover:bg-zinc-600 text-white font-inter text-sm font-medium py-2 transition-colors disabled:opacity-40"
            >
              {saving === 'note' ? 'Saving...' : 'Save Note'}
            </button>
          </div>

          {/* Customer Messages (if any) */}
          {messages.length > 0 && (
            <div className="bg-[#1a1a1a] border border-orange-500/20 p-5">
              <h2 className="font-bebas text-lg text-white tracking-wider mb-3 flex items-center gap-2">
                <MessageSquare size={16} className="text-orange-500" />
                CUSTOMER MESSAGES ({messages.length})
              </h2>
              <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
                {messages.map(m => (
                  <div key={m.message_id} className={`px-3 py-2.5 ${m.from === 'customer' ? 'bg-[#0f0f0f] border border-white/5' : 'bg-orange-500/10 border border-orange-500/20'}`}>
                    <p className={`font-inter text-[10px] mb-1 ${m.from === 'customer' ? 'text-zinc-500' : 'text-orange-400'}`}>
                      {m.from === 'customer' ? (m.sender_name || 'Customer') : 'You (Admin)'} · {m.timestamp ? new Date(m.timestamp).toLocaleString() : ''}
                    </p>
                    <p className="font-inter text-sm text-white">{m.message}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={replyMsg}
                  onChange={e => setReplyMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAdminReply()}
                  placeholder="Reply to customer..."
                  className="flex-1 bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors placeholder-zinc-600"
                />
                <button onClick={handleAdminReply} disabled={sendingReply || !replyMsg.trim()}
                  className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-bebas text-sm tracking-wider px-3 py-2 transition-colors">
                  <Send size={13} />{sendingReply ? '...' : 'REPLY'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ===== PAYMENT RECORDS ===== */}
      <div className="mt-5 bg-[#1a1a1a] border border-white/10" data-testid="payment-records-section">
        <div className="px-5 py-4 border-b border-white/10">
          <h2 className="font-bebas text-lg text-white tracking-wider flex items-center gap-2">
            <CreditCard size={16} className="text-orange-500" />
            PAYMENT RECORDS ({payments.length})
          </h2>
        </div>
        {payments.length === 0 ? (
          <div className="text-center py-8">
            <p className="font-inter text-sm text-zinc-600">No payment records yet — process payment above to create one</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {payments.map(p => (
              <div key={p.payment_id || p._id} data-testid={`payment-record-${p.payment_id}`} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
                    {[
                      { label: 'Payment ID', value: p.payment_id, mono: true },
                      { label: 'Provider', value: p.provider?.replace('_', ' ').toUpperCase() },
                      { label: 'Amount', value: `$${p.amount?.toFixed(2)} ${p.currency || 'USD'}` },
                      { label: 'Status', value: p.internal_status?.toUpperCase(), status: p.internal_status },
                    ].map(f => (
                      <div key={f.label}>
                        <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">{f.label}</p>
                        <p className={`font-inter text-sm mt-0.5 ${f.mono ? 'font-mono text-zinc-300' : ''} ${
                          f.status === 'paid' ? 'text-green-400 font-semibold' :
                          f.status === 'failed' ? 'text-red-400 font-semibold' :
                          f.status === 'refunded' ? 'text-yellow-400' :
                          'text-white'
                        }`}>{f.value || '—'}</p>
                      </div>
                    ))}
                    {p.provider_transaction_id && (
                      <div>
                        <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">Transaction ID</p>
                        <p className="font-inter text-xs text-zinc-400 font-mono mt-0.5">{p.provider_transaction_id}</p>
                      </div>
                    )}
                    {p.provider_session_id && (
                      <div>
                        <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">Session / Invoice</p>
                        <p className="font-inter text-xs text-zinc-400 font-mono mt-0.5">{p.provider_session_id}</p>
                      </div>
                    )}
                    {p.failure_reason && (
                      <div className="sm:col-span-2">
                        <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">Failure Reason</p>
                        <p className="font-inter text-xs text-red-400 mt-0.5">{p.failure_reason}</p>
                      </div>
                    )}
                    {p.refund_status && p.refund_status !== 'none' && (
                      <div>
                        <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">Refund</p>
                        <p className="font-inter text-sm text-yellow-400 mt-0.5">{p.refund_status} (${p.refund_amount?.toFixed(2)})</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {p.internal_status === 'paid' && (
                      <button
                        data-testid={`refund-payment-${p.payment_id}`}
                        onClick={async () => {
                          if (!window.confirm(`Refund $${p.amount?.toFixed(2)}?`)) return;
                          setSaving('refund');
                          try {
                            const refunded = await adminRefundPayment(token, p.payment_id, null);
                            setPayments(prev => prev.map(x => x.payment_id === p.payment_id ? refunded : x));
                            const updated = await adminGetOrder(token, id);
                            setOrder(updated);
                          } catch (err) { setError(err.message); }
                          setSaving('');
                        }}
                        disabled={!!saving}
                        className="flex items-center gap-1.5 font-inter text-xs text-yellow-400 border border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 px-3 py-1.5 transition-colors"
                      >
                        <RotateCcw size={11} />Refund
                      </button>
                    )}
                  </div>
                </div>
                {/* Payment Timeline */}
                {p.timeline?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="flex flex-wrap gap-3">
                      {p.timeline.slice(-3).map((ev, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
                          <span className="font-inter text-[11px] text-zinc-500">{ev.description}</span>
                          <span className="font-inter text-[10px] text-zinc-700">
                            {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString() : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== DOCUMENT PACKET ===== */}
      <div className="mt-5">
        <AdminDocumentPacket orderId={id} orderNumber={order.order_number} />
      </div>
    </div>
  );
}
